import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import type { ChatMessage, Presence } from '@rabbitfuel/shared';
import { timeShort } from '../lib/format.ts';
import { roleLabel } from '../lib/session.ts';

/** Ab wie vielen Pixeln Abstand zum Ende gilt man als "nicht mehr unten". */
const STICK_THRESHOLD = 48;

export function ChatPanel({
  messages,
  presence,
  onSend,
  disabled,
  headAction,
}: {
  messages: ChatMessage[];
  presence: Presence;
  onSend: (text: string) => void;
  disabled?: boolean;
  /** Knopf rechts im Kopf -- die Regie legt dort das Einklappen ab. */
  headAction?: ReactNode;
}) {
  const [text, setText] = useState('');
  const [unread, setUnread] = useState(0);
  const logRef = useRef<HTMLDivElement | null>(null);
  // Klebt die Ansicht am unteren Rand? Startet true, damit man den neuesten
  // Stand sieht und nicht oben im Verlauf landet.
  const stickRef = useRef(true);
  /*
   * Gezaehlt wird ab der letzten Nachricht, nicht ueber die Laenge: der
   * Verlauf ist gedeckelt (realtime.tsx), und ab dann bleibt die Laenge
   * gleich, waehrend oben eine Nachricht geht und unten eine kommt. Mit der
   * Laenge als Signal scrollte der Chat ab da nicht mehr mit.
   */
  const lastSeenId = useRef<string | null>(null);
  /** Die oberste sichtbare Nachricht beim Zurueckscrollen -- siehe `keepAnchor`. */
  const anchorRef = useRef<{ id: string; offset: number } | null>(null);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const el = logRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  /** Merkt sich, welche Nachricht oben steht und wie weit sie angeschnitten ist. */
  const rememberAnchor = () => {
    const el = logRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    for (const child of el.children) {
      const rect = child.getBoundingClientRect();
      const id = (child as HTMLElement).dataset.id;
      if (id && rect.bottom > top) {
        anchorRef.current = { id, offset: rect.top - top };
        return;
      }
    }
    anchorRef.current = null;
  };

  /*
   * Wer zurueckgescrollt hat und liest, soll nicht wegrutschen, wenn oben
   * eine alte Nachricht aus dem gedeckelten Verlauf faellt. Chrome haelt die
   * Stelle von selbst, Safari nicht -- deshalb von Hand und im Stylesheet
   * `overflow-anchor: none`, damit beides sich nicht addiert.
   */
  const keepAnchor = () => {
    const el = logRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;
    const child = el.querySelector<HTMLElement>(`[data-id="${CSS.escape(anchor.id)}"]`);
    if (!child) return;
    const drift = child.getBoundingClientRect().top - el.getBoundingClientRect().top - anchor.offset;
    if (drift) el.scrollTop += drift;
  };

  const handleScroll = () => {
    const el = logRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_THRESHOLD;
    stickRef.current = atBottom;
    if (atBottom) {
      setUnread(0);
      anchorRef.current = null;
    } else {
      rememberAnchor();
    }
  };

  // Vor dem Paint scrollen, sonst blitzt die alte Position kurz auf.
  const lastId = messages.length ? messages[messages.length - 1].id : null;
  useLayoutEffect(() => {
    const previous = lastSeenId.current;
    lastSeenId.current = lastId;
    if (!lastId || lastId === previous) return;
    if (stickRef.current) {
      scrollToBottom();
      return;
    }
    keepAnchor();
    // Neu ist alles hinter der zuletzt gesehenen -- ist die schon aus dem
    // Verlauf gefallen, ist der ganze Verlauf neu.
    const seenAt = previous ? messages.findIndex((m) => m.id === previous) : -1;
    const added = seenAt >= 0 ? messages.length - 1 - seenAt : messages.length;
    setUnread((u) => Math.min(99, u + added));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId]);

  // Der Verlauf kommt gebuendelt im welcome an -- danach ans Ende springen.
  // Bilder oder Umbrueche koennen die Hoehe noch aendern, daher zwei Anlaeufe.
  useEffect(() => {
    scrollToBottom();
    const raf = requestAnimationFrame(() => stickRef.current && scrollToBottom());
    return () => cancelAnimationFrame(raf);
  }, []);

  // Wird das Panel groesser oder kleiner (Fenster, Tastatur), unten bleiben.
  useEffect(() => {
    const el = logRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottom();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const clean = text.trim();
    if (!clean) return;
    onSend(clean);
    setText('');
    // Wer selbst schreibt, will das Ergebnis sehen.
    stickRef.current = true;
    setUnread(0);
    scrollToBottom('smooth');
  };

  return (
    <aside className="chat">
      <div className="chat__head">
        <h3>Hauptchat</h3>
        <HelpLink topic="chat" />
        <span className="count">
          {presence.total} online · {presence.athletes} Athlet:innen
        </span>
        {headAction}
      </div>

      <div className="chat__body">
        <div className="chat__log" ref={logRef} onScroll={handleScroll}>
          {messages.length === 0 && <p className="msg msg--system">Noch nichts geschrieben. Mach den Anfang.</p>}
          {messages.map((m) =>
            m.system ? (
              <p key={m.id} data-id={m.id} className="msg msg--system">
                {m.text}
              </p>
            ) : (
              <p key={m.id} data-id={m.id} className={`msg msg--${m.role}`}>
                <span className="msg__role">{roleLabel[m.role].slice(0, 4)}</span>
                <span className="msg__who">{m.displayName}</span>
                {m.text}
                <span className="msg__time">{timeShort.format(m.ts)}</span>
              </p>
            ),
          )}
        </div>

        {unread > 0 && (
          <button
            type="button"
            className="chat__jump"
            onClick={() => {
              stickRef.current = true;
              setUnread(0);
              scrollToBottom('smooth');
            }}
          >
            {unread >= 99 ? '99+' : unread} neue {unread === 1 ? 'Nachricht' : 'Nachrichten'} ↓
          </button>
        )}
      </div>

      <form className="chat__form" onSubmit={submit}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={disabled ? 'nicht verbunden' : 'Nachricht an alle …'}
          maxLength={500}
          disabled={disabled}
        />
        <button className="btn btn--sm" type="submit" disabled={disabled || !text.trim()}>
          Senden
        </button>
      </form>
    </aside>
  );
}
